import { IconButton, Stack, Typography } from '@mui/material';
import {
  FilterKeys,
  FilterSection,
  GetParentsQueryFilters,
  PARENT_QUERY_FILTER_KEYS,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupExpandedSelect,
  DateDropdown,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  GenderExpandedSelect,
  GenderTagSelect,
  InviteStatusExpandedSelect,
  InviteStatusTagSelect,
  NationalityExpandedSelect,
  NationalityTagSelect,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  PropertyTypeExpandedSelect,
  PropertyTypeTagSelect,
  renderPropertyGroupTags,
} from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { CloseSmallIcon } from '@schooly/style';
import { FC, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { ParentsFiltersSidebar } from './ParentsFiltersSidebar';
import { useParentsFilters } from './useParentsFilters';

type ParentsFiltersProps = {
  schoolId: string;
  filters: GetParentsQueryFilters;
  defaultFilters: GetParentsQueryFilters;
  onSetFilters: (v: GetParentsQueryFilters) => void;
  defaultUserFilters: GetParentsQueryFilters;
};

export const ParentsFilters: FC<ParentsFiltersProps> = ({
  defaultFilters,
  filters,
  schoolId,
  onSetFilters,
  defaultUserFilters,
}) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff } = useAuth();

  const {
    labels,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersAgeGroup,
    filtersStatus,
    filtersHouse,
    filtersGender,
    filtersNationality,
    filtersInviteStatus,
    filtersOnlyEmptyEmail,
    onSetHouseId,
    onClearHouse,
    onSetGender,
    onClearGender,
    onSetNationality,
    onClearNationality,
    onSetStatusId,
    onClearStatus,
    onSetInviteStatus,
    onClearInviteStatus,
    getSchoolLevelById,
    getAgeGroupById,
    getAgeGroupsByLevelId,
    onSetDate,
    onSetAgeGroup,
    onClearAgeGroup,
    onClearOnlyEmptyEmail,
  } = useParentsFilters({ schoolId, filters, onSetFilters });

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  return (
    <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
      <PersonalFiltersDropdown
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.Parent}
        filters={filters}
        onSetFilters={(v) => {
          const appliedFilters = pickOnlyParamsFromFilterKeys(PARENT_QUERY_FILTER_KEYS, v);

          onSetFilters({
            ...appliedFilters,
            [FilterKeys.Date]: appliedFilters.date || defaultFilters.date,
          });
        }}
      />
      {filtersDate && (
        <DateDropdown date={filtersDate} label={labels[FilterKeys.Date]} onSetDate={onSetDate} />
      )}
      {!!filtersStatus?.length && (
        <FilterDropdown
          onClear={onClearStatus}
          label={labels[FilterKeys.Status]}
          tags={(open) =>
            filtersStatus.map((id) => (
              <PropertyTypeTagSelect
                userRole={SchoolUserRole.Student}
                schoolId={schoolId}
                sx={{ maxWidth: 200 }}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <PropertyTypeExpandedSelect
              propertyType={SchoolPropertyType.Status}
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersStatus}
              onApply={onSetStatusId}
              onClose={onClose}
              onClear={onClearStatus}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersAgeGroup?.length && (
        <FilterDropdown
          onClear={onClearAgeGroup}
          label={labels[FilterKeys.AgeGroup]}
          tags={(open) =>
            renderPropertyGroupTags({
              selectedItems: selectedItemsForAgeGroups,
              onClick: open,
              getProperty: (i) =>
                i.isGroup
                  ? getSchoolLevelById(i.id)
                  : { ...getAgeGroupById(i.id), type: SchoolPropertyType.AgeGroup },
              getTooltip: (i) =>
                i.isGroup
                  ? getAgeGroupsByLevelId(i.id).map((ageGroup) => (
                      <Typography key={ageGroup.id}>{ageGroup.name}</Typography>
                    ))
                  : null,
              tagProps: {
                userRole: SchoolUserRole.Student,
                sx: { maxWidth: 200 },
              },
            })
          }
        >
          {(onClose) => (
            <AgeGroupExpandedSelect
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersAgeGroup}
              onApply={onSetAgeGroup}
              onClose={onClose}
              onClear={onClearAgeGroup}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersHouse?.length && (
        <FilterDropdown
          onClear={onClearHouse}
          label={labels[FilterKeys.House]}
          tags={(open) =>
            filtersHouse.map((id) => (
              <PropertyTypeTagSelect
                userRole={SchoolUserRole.Student}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <PropertyTypeExpandedSelect
              propertyType={SchoolPropertyType.House}
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersHouse}
              onApply={onSetHouseId}
              onClose={onClose}
              onClear={onClearHouse}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersNationality?.length && (
        <FilterDropdown
          onClear={onClearNationality}
          label={labels[FilterKeys.Nationality]}
          tags={(open) =>
            filtersNationality.map((v) => (
              <NationalityTagSelect sx={{ maxWidth: 200 }} key={v} nationality={v} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <NationalityExpandedSelect
              selectedNationalities={filtersNationality}
              onApply={onSetNationality}
              onClose={onClose}
              onClear={onClearNationality}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersGender?.length && (
        <FilterDropdown
          onClear={onClearGender}
          label={labels[FilterKeys.Gender]}
          tags={(open) =>
            filtersGender.map((v) => (
              <GenderTagSelect sx={{ maxWidth: 200 }} key={v} gender={v} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <GenderExpandedSelect
              selectedGenders={filtersGender}
              onApply={onSetGender}
              onClose={onClose}
              onClear={onClearGender}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersInviteStatus?.length && (
        <FilterDropdown
          onClear={onClearInviteStatus}
          label={labels[FilterKeys.InviteStatus]}
          tags={(open) =>
            filtersInviteStatus.map((status) => (
              <InviteStatusTagSelect
                status={status}
                sx={{ maxWidth: 200 }}
                key={status}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <InviteStatusExpandedSelect
              selectedStatuses={filtersInviteStatus}
              onApply={onSetInviteStatus}
              onClose={onClose}
              onClear={onClearInviteStatus}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersOnlyEmptyEmail?.[0] && (
        <Stack direction="row" gap={1}>
          <Typography variant="h3">
            <FormattedMessage id="peopleDetail-NoEmail" />
          </Typography>
          <IconButton
            sx={{ fontSize: (theme) => theme.spacing(2) }}
            onClick={onClearOnlyEmptyEmail}
          >
            <CloseSmallIcon />
          </IconButton>
        </Stack>
      )}
      <ParentsFiltersSidebar
        ref={sidebar}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={onSetFilters}
        schoolId={schoolId}
      />
    </FiltersContainer>
  );
};
